import { Koi } from '@ksyos/koi';
import type { OrganizationTypeWithLinkedProfessions } from '@common/enums';
import {
    Gender,
    organizationTypesWithRightsToCreateAccountsAndEndEmployments,
    Profession,
    SpecialismCode,
} from '@common/enums';
import { MatchingEntityResultCode } from '@common/enums/matchingEntityResultCode';
import type { ISchemaContainer } from './schemaContainer';
import { replaceKoiError } from './replaceKoiError';
import { emailSchema } from './schemas';
import {
    validateIdentifierDeclarationField,
    validateIdentifierDeclarationPractitionerIdField,
    validateIdentifierRegistrationBigField,
    validateIdentifierRegistrationKpField,
    validateZorgmailNummerField,
} from './utils/accountFieldsCustomValidationFunctions';

export interface AccountRequestAddValidated {
    approverPractitionerId: string | null;
    employment: {
        identifierDeclarationPractitionerId: string | null;
        profession: Profession;
        specialismCode: SpecialismCode | null;
        startDate: Date | string;
        zorgmailNummer: string | null;
    };
    hasMatchedOnNameAndGender: boolean;
    isUnderstandingChecked: boolean;
    matchingAccountRequestId: string | null;
    matchingPractitionerHasEmailAddress: boolean | null;
    matchingResultCode: MatchingEntityResultCode;
    needsKsyosApproval: boolean;
    organizationType: OrganizationTypeWithLinkedProfessions;
    practitioner: {
        emailAddress: string;
        gender: Gender;
        identifierDeclaration: string | null;
        identifierRegistrationBig: string | null;
        identifierRegistrationKp: string | null;
        nameFamily: string;
        nameGiven: string | null;
        nameInfix: string | null;
    };
    practitionerId: string | null;
}

export interface AccountRequestRejectValidated {
    rejectionReason: string;
}

export interface AccountRequestUpdateApproverValidated {
    approverPractitionerId: string | null;
    needsKsyosApproval: boolean;
}

export type AccountRequestMatch = {
    entity: 'matchingAccountRequestId' | 'practitionerId';
    hasPractitionerEmailAddress: boolean;
    id: string;
};

export interface AccountRequestApproveValidated {
    match: AccountRequestMatch | null;
}

export const accountRequestAddSchema: ISchemaContainer<AccountRequestAddValidated> = {
    schema: Koi.object({
        approverPractitionerId: Koi.when('needsKsyosApproval', {
            is: false,
            otherwise: Koi.valid(null),
            then: Koi.string().uuid().required(),
        }),
        employment: Koi.object({
            identifierDeclarationPractitionerId: Koi.custom(
                (value, helpers) => validateIdentifierDeclarationPractitionerIdField(value, helpers),
                'identifierDeclarationPractitionerId custom validation'
            ),
            profession: Koi.string()
                .valid(...Object.values(Profession))
                .required(),
            specialismCode: Koi.when('profession', {
                is: Profession.medicalSpecialist,
                otherwise: Koi.valid(null),
                then: Koi.koi().enum(SpecialismCode),
            }),
            startDate: Koi.string().isoDate().required(),
            zorgmailNummer: Koi.custom(
                (value, helpers) => validateZorgmailNummerField(value, helpers),
                'zorgmailNummer custom validation'
            ),
        }),
        hasMatchedOnNameAndGender: Koi.boolean().default(false).required(),
        isUnderstandingChecked: Koi.boolean().valid(true),
        matchingAccountRequestId: Koi.string().uuid().allow(null),
        matchingPractitionerHasEmailAddress: Koi.boolean().allow(null),
        matchingResultCode: Koi.koi().enum(MatchingEntityResultCode),
        needsKsyosApproval: Koi.boolean().required(),
        organizationType: Koi.string().valid(...organizationTypesWithRightsToCreateAccountsAndEndEmployments),
        practitioner: Koi.object({
            emailAddress: Koi.when('/matchingPractitionerHasEmailAddress', {
                is: true,
                otherwise: emailSchema.required(),
                then: emailSchema.allow(null),
            }),
            gender: Koi.string()
                .valid(...Object.values(Gender))
                .required(),
            identifierDeclaration: Koi.when('...matchingResultCode', {
                is: Koi.valid(
                    MatchingEntityResultCode.ACTIVE_EMPLOYMENT_ON_SAME_ORGANIZATION,
                    MatchingEntityResultCode.PENDING_REQUEST_ON_SAME_ORGANIZATION
                ),
                otherwise: Koi.custom(
                    (value, helpers) => validateIdentifierDeclarationField(value, helpers),
                    'identifierDeclaration custom validation'
                ),
                then: Koi.valid(null).error((errors) =>
                    replaceKoiError(errors, 'any.only', 'IDENTIFIER_DECLARATION_EXISTS')
                ),
            }),
            identifierRegistrationBig: Koi.custom(
                (value, helpers) => validateIdentifierRegistrationBigField(value, helpers, true),
                'identifierRegistrationBig custom validation'
            ),
            identifierRegistrationKp: Koi.when('...matchingResultCode', {
                is: Koi.valid(
                    MatchingEntityResultCode.ACTIVE_EMPLOYMENT_ON_SAME_ORGANIZATION,
                    MatchingEntityResultCode.PENDING_REQUEST_ON_SAME_ORGANIZATION
                ),
                otherwise: Koi.custom(
                    (value, helpers) => validateIdentifierRegistrationKpField(value, helpers),
                    'identifierRegistrationKp custom validation'
                ),
                then: Koi.valid(null).error((errors) =>
                    replaceKoiError(errors, 'any.only', 'IDENTIFIER_DECLARATION_EXISTS')
                ),
            }),
            nameFamily: Koi.when('...matchingResultCode', {
                is: Koi.valid(
                    MatchingEntityResultCode.PENDING_REQUEST_ON_SAME_ORGANIZATION,
                    MatchingEntityResultCode.ACTIVE_EMPLOYMENT_ON_SAME_ORGANIZATION
                ),
                otherwise: Koi.string(),
                then: Koi.when('identifierDeclaration', {
                    is: null,
                    otherwise: Koi.string(),
                    then: Koi.valid(null).error((errors) =>
                        replaceKoiError(errors, 'any.only', 'practitionerAlreadyExisting')
                    ),
                }),
            }),
            nameGiven: Koi.string().allow(null),
            nameInfix: Koi.string().allow(null),
        }),
        practitionerId: Koi.string().uuid().allow(null),
    }).options({ convert: false, presence: 'required' }),
};

export const accountRequestRejectSchema: ISchemaContainer<AccountRequestRejectValidated> = {
    schema: Koi.object({
        rejectionReason: Koi.string().max(255).required(),
    }).options({ convert: false, presence: 'required' }),
};

export const accountRequestUpdateApproverSchema: ISchemaContainer<AccountRequestUpdateApproverValidated> = {
    schema: Koi.object({
        approverPractitionerId: Koi.when('needsKsyosApproval', {
            is: false,
            otherwise: Koi.valid(null),
            then: Koi.string().uuid().required(),
        }),
        needsKsyosApproval: Koi.boolean().required(),
    }).options({ convert: false, presence: 'required' }),
};

export const accountRequestApproveSchema: ISchemaContainer<AccountRequestApproveValidated> = {
    schema: Koi.object({
        match: Koi.object({
            entity: Koi.string().valid('matchingAccountRequestId', 'practitionerId'),
            hasPractitionerEmailAddress: Koi.boolean().required(),
            id: Koi.string().uuid().required(),
        }).allow(null),
    }).options({ convert: false, presence: 'required' }),
};
